import React, { useState, useEffect } from 'react';

const Solutions = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Farmnet helps farmers optimize growing conditions—boosting yields and resilience through smart environmental monitoring and control.";

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setIsTyping(false);
          setTimeout(() => {
            backspaceText();
          }, 2000);
        }, 1000);
      }
    };

    const backspaceText = () => {
      let backspaceInterval;
      let backspaceIndex = fullText.length;

      const eraseText = () => {
        if (backspaceIndex >= 0) {
          setText(fullText.substring(0, backspaceIndex));
          backspaceIndex--;
        } else {
          clearInterval(backspaceInterval);
          setIsTyping(true);
          currentIndex = 0;
          typingInterval = setInterval(typeText, 50);
        }
      };

      backspaceInterval = setInterval(eraseText, 20);
    };

    if (isTyping) {
      typingInterval = setInterval(typeText, 50);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isTyping, fullText]);

  return (
    <div className="relative bg-[#F3F3F3]">
      {/* Content */}
      <div className="relative z-10 px-4 md:px-24 pb-10 md:pt-5 pt-1">
        {/* Heading */}
        <div className="mt-2 md:mt-2 md:mb-5 flex justify-between items-center p mb-2">
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 ">
          <span className='text-[#152C24]'>INSIGHTS & INNOVATIONS</span>
          </h1>
          <div>
            <div className='flex md:gap-2 gap-1 justify-center md:mt-2'>
              <div className='bg-[#CFD7D7] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
              <div className="bg-[#2E453D] border-b-2 border-[#FFBD26] md:ml-0 ml-2 text-white font-normal md:px-6 px-2 rounded-md md:rounded-[30px] w-[70px] h-[15px] md:w-[150px] md:text-[20px] text-[6px] md:h-[40px] mt-0 md:-mt-[1px] flex justify-between items-center">
                See More
              </div>
              <div className='bg-[#2E453D] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 mr-2 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
            </div>
          </div>
        </div>

        {/* Records Section */}
        <div className="rounded-md w-full md:h-[630px] h-[175px] relative flex items-center justify-center p-2 md:p-0 pt-2 md:mt-4">
          <div className="rounded-xl overflow-hidden relative w-full h-[180px] md:mt-5 md:h-[700px] flex justify-center items-center cursor-pointer">
              <>
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751380763/image-2_crixjs.png"
                  alt="Mobile"
                  className="object-cover w-full h-full"
                />
              </>
          </div>
        </div>

        {/* Description */}
        <div className='md:text-[32px] text-[10px] font-normal md:mt-16 mt-2 space-y-6'>
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 text-[#979797]">
            INSIGHTS & INNOVATIONS | <span className='text-[#152C24]'>Environmental Control System</span>
          </h1>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🌱 What Are Environmental Control Systems in Agriculture?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Environmental control systems are technologies or methods used to monitor and regulate the conditions in which crops grow—such as temperature, humidity, light, soil moisture, and CO₂ levels—to optimize plant health and productivity.
          </p>
          
          <p className="md:text-[32px] text-[8px]">
            These systems are commonly used in greenhouses, controlled-environment agriculture (CEA), and increasingly in open-field settings through affordable, digital tools.
          </p>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🌍 How Do Smallholder Farmers in Africa Use Environmental Control Systems?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Although high-tech versions (like fully automated greenhouses) are expensive, smallholder farmers in Africa are adopting low-cost, scalable environmental control solutions tailored to their needs and context. Here's how:
          </p>
          
          <ol className="md:text-[32px] text-[8px] list-decimal pl-5 space-y-3 [&>li::marker]:font-bold">
            <li>
              <span className="font-bold">Mobile Weather Apps and SMS Alerts</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use apps or SMS services to receive real-time weather forecasts</li>
                <li>Helps plan irrigation, spraying, and harvesting to avoid losses from heat, rain, or drought</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Soil and Moisture Sensors</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Affordable sensors help know when and how much to water</li>
                <li>Prevents over- or under-irrigation</li>
                <li>Saves water and boosts yields in water-scarce areas</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Shade Nets and Tunnels</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Used in high-heat areas to control sunlight exposure</li>
                <li>Protects crops from extreme weather</li>
                <li>Maintains better temperature and humidity for sensitive crops</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Manual Ventilation and Irrigation Systems</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Simple systems to control air flow and irrigation timing</li>
                <li>Includes opening vents or using gravity-fed drip systems</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Solar-Powered Environmental Tools</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Solar pumps and sensors work in off-grid areas</li>
                <li>Provides access to irrigation water and monitoring</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Decision Support Apps</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Apps like Farmnet provide advice based on environmental data</li>
                <li>Recommends optimal planting, fertilizing, and protection times</li>
              </ul>
            </li>
          </ol>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🔍 Role of Farmnet in Environmental Control
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Farmnet helps smallholder farmers indirectly manage environmental conditions by:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Providing localized weather forecasts and agro-advisory</li>
            <li>Helping optimize irrigation and planting times through crop calendars</li>
            <li>Linking farmers to partners who can support with greenhouse tech or sensors</li>
            <li>Educating users on low-cost control methods (mulching, water conservation, shading)</li>
          </ul>
          
         <div>
         <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            ✅ Impact on Smallholders
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Using even basic environmental control methods allows smallholder farmers to:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc font-bold pl-10 space-y-2">
            <li>Improve yields and crop quality</li>
            <li>Reduce input waste (water, fertilizer)</li>
            <li>Minimize risk from climate variability</li>
            <li>Grow high-value crops like vegetables and herbs year-round</li>
          </ul>
         </div>
        </div>
        
        <div className='flex justify-end items-center'>
          <div>
            <h1 className="text-[18px] md:text-[32px] font-bold mt-2 mb-4 text-black">
              Download App
            </h1>
            <div className="md:w-[450px]">
              <div className="flex flex-row justify-between space-x-2 items-center sm:space-y-0">
                <a href="https://play.google.com/store/apps/details?id=com.farmnet.africa&pcampaignid=web_share" alt="none">   
             <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751889690/google_PLAY-LOGO.WINE_emyxpu.png" className="h-[25px] md:w-[280px] md:h-[180px]" alt="App Store" />    
             <p className="md:text-[14px] text-[6px] md:-mt-12 md:pl-5 font-normal md:px-1">
             Click to download App
  </p>
     </a>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744414874/image_scynki.png"
                  alt="Google Play"
                  className="h-[30px] md:h-[75px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
        <div className='md:h-10 h-5'></div>
      </div>
    </div>
  );
};

export default Solutions;